import React, { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { Pagination, Table as AntdTable, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import Truncate from 'v2/components/Shared/TruncateText';
import type { IRefund } from 'v2/interfaces/reports/refunds';
import { refundsService } from 'v2/services/reports/refunds';

import { Icon } from '@eduzz/ui-antd-theme';

export const Table = () => {
  useObservable(() => refundsService.loadRefunds(), []);
  const [refunds] = useObservable(() => refundsService.getRefunds(), []);
  const [isLoading] = useObservable(() => refundsService.isLoading(), []);
  const [pagination] = useObservable(() => refundsService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (perPage: number) => {
      refundsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
      document.getElementById('refunds-table')?.scrollIntoView();
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      refundsService.setPagination({ ...pagination, current_page: page });
      document.getElementById('refunds-table')?.scrollIntoView();
    },
    [pagination]
  );

  const columns: ColumnsType<IRefund> = useMemo(
    () => [
      {
        title: 'Fatura',
        dataIndex: 'invoice_id',
        key: 'invoice_id',
        width: 90,
        render: (value: number) => <Typography.Text>{value}</Typography.Text>
      },
      {
        title: 'Produto',
        key: 'product',
        render: (_: unknown, refund) => (
          <div className='flex flex-col items-start gap-1'>
            <Typography.Text>
              <Truncate count={30} final='...'>
                {refund?.product_name || '-'}
              </Truncate>
            </Typography.Text>
            <Typography.Text type='secondary'>({refund?.product_id || '-'})</Typography.Text>
            {!!refund.partial_refund && (
              <Tag icon={<Icon.DollarSign size={14} />} className='m-0'>
                Reembolso Parcial
              </Tag>
            )}
          </div>
        )
      },
      {
        title: 'Pagamento em',
        dataIndex: 'received_at',
        key: 'received_at',
        width: 120,
        render: (value: string) => (
          <div className='flex flex-col items-start gap-1'>
            <Typography.Text>{formatDate(value, 'dd/MM/yyyy')}</Typography.Text>
            <Typography.Text type='secondary'>{formatDate(value, 'HH:mm')}</Typography.Text>
          </div>
        )
      },
      {
        title: 'Reembolso em',
        dataIndex: 'refunded_at',
        key: 'refunded_at',
        width: 120,
        render: (value: string) => (
          <div className='flex flex-col items-start gap-1'>
            <Typography.Text>{formatDate(value, 'dd/MM/yyyy')}</Typography.Text>
            <Typography.Text type='secondary'>{formatDate(value, 'HH:mm')}</Typography.Text>
          </div>
        )
      },
      {
        title: 'Dias Reembolso',
        dataIndex: 'refund_days',
        key: 'refund_days',
        width: 130,
        render: (value: number) => <Typography.Text>{value || 0}</Typography.Text>
      },
      {
        title: 'Motivo',
        dataIndex: 'motive',
        key: 'motive',
        render: (value: string) => <Typography.Text>{value || '-'}</Typography.Text>
      },
      {
        title: 'Valor da Venda',
        dataIndex: 'total_refunded',
        key: 'total_refunded',
        width: 130,
        align: 'right',
        render: (value: number) => (
          <Typography.Text strong>
            <FormattedNumber value={value || 0} format='BRL' />
          </Typography.Text>
        )
      }
    ],
    []
  );

  return (
    <div id='refunds-table' data-testid='refunds-table' className='w-full overflow-x-auto overflow-y-hidden'>
      <AntdTable<IRefund>
        className='min-w-225 [&_.ant-table-body]:overflow-y-hidden [&_.ant-table-content]:overflow-y-hidden [&_.ant-table-tbody_.ant-typography]:text-sm'
        rowKey={record => String(record.invoice_id)}
        columns={columns}
        dataSource={(refunds as IRefund[]) || []}
        loading={!!isLoading}
        pagination={false}
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
      />
      {pagination && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            current={pagination.current_page || 1}
            total={pagination.total_items || 0}
            pageSize={pagination.per_page || 10}
            pageSizeOptions={['10', '25', '50']}
            showSizeChanger
            onChange={onChangePage}
            onShowSizeChange={(_, size) => onChangePerPage(size)}
          />
        </div>
      )}
    </div>
  );
};
